import { useCallback } from 'react';

import { ActivitiesContentType } from 'global/constants';
import type {
  DuplicateActivitiesSectionMutation,
  DuplicateActivitiesSectionMutationVariables,
} from './activity-builder-section-duplicate.graphql.generated';
import { useActivityBuilderApolloContext } from './activity-builder.apollo.context';
import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import {
  DUPLICATE_ACTIVITIES_SECTION,
  DUPLICATE_ACTIVITIES_SECTION_AS_SUB_SECTION,
  DUPLICATE_ACTIVITIES_SUB_SECTION,
  DUPLICATE_ACTIVITIES_SUB_SECTION_AS_SECTION,
} from './activity-builder-section-duplicate.graphql';
import type {
  DuplicateActivitiesSectionAsSubSectionInput,
  DuplicateActivitiesSectionInput,
  DuplicateActivitiesSubSectionAsSectionInput,
  DuplicateActivitiesSubSectionInput,
} from '../../gql/schema-types';
import type {
  DuplicateActivitiesSubSectionMutation,
  DuplicateActivitiesSubSectionMutationVariables,
} from './activity-builder-section-duplicate.graphql.generated';
import type {
  DuplicateActivitiesSubSectionAsSectionMutation,
  DuplicateActivitiesSubSectionAsSectionMutationVariables,
} from './activity-builder-section-duplicate.graphql.generated';
import {
  addSectionOverviewToLesson,
  modifyActivitiesPublishChanges,
  resetInteractiveContentInCache,
  upsertSectionOverview,
} from './activity-builder.apollo.utils';
import type { LessonActivitiesFragment } from './lesson-builder.graphql.generated';
import type {
  DuplicateActivitiesSectionAsSubSectionMutation,
  DuplicateActivitiesSectionAsSubSectionMutationVariables,
} from './activity-builder-section-duplicate.graphql.generated';
import { useActivityBuilderRequest } from './activity-builder-request.apollo';
import { useGetDraftActivitiesFromCache } from './logic/hooks/use-get-draft-activities-from-cache';
import { useGetInteractiveLessonParams } from './activity-builder.hooks';
import type { Pluck } from 'lib/gql/pluck';

export const useDuplicateActivity = ({
  contentId,
  contentType,
  onError,
  onCompleted,
}: {
  contentId: number;
  contentType: ActivitiesContentType;
  onCompleted?: (data: DuplicateActivitiesSectionMutation) => void;
  onError?: () => void;
}) => {
  const { userClassId } = useGetInteractiveLessonParams();
  const { createRequest, clearRequest } = useActivityBuilderRequest();

  const interactiveLesson = useGetDraftActivitiesFromCache({
    contentId,
    contentType,
  });
  const { interactiveLessonClone, setInteractiveLessonClone } =
    useActivityBuilderApolloContext();

  const [doDuplicateSection, { data, error, loading, client }] =
    useLoggingMutation<
      DuplicateActivitiesSectionMutation,
      DuplicateActivitiesSectionMutationVariables
    >(DUPLICATE_ACTIVITIES_SECTION, {
      refetchQueries: [
        'GetLessonTeachingAndLearningSequence',
        'GetAssessmentTeachingAndLearningSequence',
        'GetDraftLessonActivities',
        'GetDraftAssessmentActivities',
      ],
      onCompleted: res => {
        if (interactiveLessonClone) {
          setInteractiveLessonClone(null);
        }
        onCompleted?.(res);
        modifyActivitiesPublishChanges({
          client,
          id: contentId,
          contentType,
        });
      },
      onError: () => {
        resetInteractiveContentInCache({
          client,
          contentType,
          classId: userClassId,
          interactiveContent: interactiveLessonClone,
        });
        onError?.();
      },
    });

  const duplicateActivity = useCallback(
    (input: DuplicateActivitiesSectionInput) => {
      const requestId = createRequest(input.sourceSectionId);
      if (!interactiveLesson) return;

      setInteractiveLessonClone(interactiveLesson);

      const sourceSectionOverview = interactiveLesson.sectionsOverview.find(
        s => s.id === input.sourceSectionId
      );

      if (sourceSectionOverview) {
        const subSections = sourceSectionOverview.subSectionsOverview;

        upsertSectionOverview({
          client,
          sectionOverview: {
            ...sourceSectionOverview,
            id: input.newDestinationSectionId,
            sort: input.destinationSectionNewSort,
            subSectionsOverview: subSections
              ? subSections.map((subSection, index) => ({
                  ...subSection,
                  id: input.newDestinationSubSectionIds?.[index] || '',
                  parentSectionId: input.newDestinationSectionId,
                }))
              : null,
          },
        });
        addSectionOverviewToLesson({
          client,
          contentId: interactiveLesson.id,
          sectionId: input.newDestinationSectionId,
          contentType,
        });
      }

      doDuplicateSection({
        variables: {
          input,
        },
      })
        .catch(() => {})
        .finally(() => {
          clearRequest(requestId);
        });
    },
    [
      doDuplicateSection,
      interactiveLesson,
      client,
      setInteractiveLessonClone,
      contentType,
      createRequest,
      clearRequest,
    ]
  );

  return {
    data,
    loading,
    error,
    duplicateActivity,
  };
};

export const useDuplicateSubActivity = ({
  contentId,
  contentType,
  onError,
  onCompleted,
}: {
  contentId: number;
  contentType: ActivitiesContentType;
  onCompleted?: (data: DuplicateActivitiesSubSectionMutation) => void;
  onError: () => void;
}) => {
  const { userClassId } = useGetInteractiveLessonParams();
  const { createRequest, clearRequest } = useActivityBuilderRequest();
  const interactiveLesson = useGetDraftActivitiesFromCache({
    contentId,
    contentType,
  });
  const { interactiveLessonClone, setInteractiveLessonClone } =
    useActivityBuilderApolloContext();

  const [duplicate, { data, error, loading, client }] = useLoggingMutation<
    DuplicateActivitiesSubSectionMutation,
    DuplicateActivitiesSubSectionMutationVariables
  >(DUPLICATE_ACTIVITIES_SUB_SECTION, {
    refetchQueries: [
      'GetLessonTeachingAndLearningSequence',
      'GetAssessmentTeachingAndLearningSequence',
      'GetDraftLessonActivities',
      'GetDraftAssessmentActivities',
    ],
    onCompleted: res => {
      onCompleted?.(res);
      modifyActivitiesPublishChanges({
        client,
        id: contentId,
        contentType,
      });
    },
    onError: () => {
      resetInteractiveContentInCache({
        client,
        contentType,
        classId: userClassId,
        interactiveContent: interactiveLessonClone,
      });
      onError?.();
    },
  });

  const duplicateSubActivity = useCallback(
    (
      input: DuplicateActivitiesSubSectionInput,
      subSection: Pluck<
        LessonActivitiesFragment,
        'sectionsOverview.subSectionsOverview'
      >
    ) => {
      if (!interactiveLesson) return;

      const requestId = createRequest(input.newDestinationSubSectionId);

      setInteractiveLessonClone(interactiveLesson);

      // find the parent section
      const parentOverview = interactiveLesson.sectionsOverview.find(
        s => s.id === subSection.parentSectionId
      );

      if (parentOverview) {
        const subSectionsOverview = parentOverview?.subSectionsOverview || [];
        upsertSectionOverview({
          client,
          sectionOverview: {
            ...parentOverview,
            subSectionsOverview: [
              ...subSectionsOverview,
              {
                ...subSection,
                id: input.newDestinationSubSectionId,
                sort: input.destinationSubSectionNewSort,
              },
            ],
          },
        });
      }

      duplicate({
        variables: {
          input,
        },
      })
        .catch(() => {})
        .finally(() => {
          clearRequest(requestId);
        });
    },
    [
      client,
      duplicate,
      interactiveLesson,
      setInteractiveLessonClone,
      createRequest,
      clearRequest,
    ]
  );

  return {
    data,
    loading,
    error,
    duplicateSubActivity,
  };
};

export const useDuplicateSubActivityAsActivity = ({
  contentId,
  contentType,
  onError,
  onCompleted,
}: {
  contentId: number;
  contentType: ActivitiesContentType;
  onCompleted?: (data: DuplicateActivitiesSubSectionAsSectionMutation) => void;
  onError: () => void;
}) => {
  const { userClassId } = useGetInteractiveLessonParams();
  const interactiveLesson = useGetDraftActivitiesFromCache({
    contentId,
    contentType,
  });
  const { interactiveLessonClone, setInteractiveLessonClone } =
    useActivityBuilderApolloContext();

  const { createRequest, clearRequest } = useActivityBuilderRequest();

  const [duplicate, { data, error, loading, client }] = useLoggingMutation<
    DuplicateActivitiesSubSectionAsSectionMutation,
    DuplicateActivitiesSubSectionAsSectionMutationVariables
  >(DUPLICATE_ACTIVITIES_SUB_SECTION_AS_SECTION, {
    refetchQueries: [
      'GetLessonTeachingAndLearningSequence',
      'GetAssessmentTeachingAndLearningSequence',
      'GetDraftLessonActivities',
      'GetDraftAssessmentActivities',
    ],
    onCompleted: res => {
      onCompleted?.(res);
      modifyActivitiesPublishChanges({
        client,
        id: contentId,
        contentType,
      });
    },
    onError: () => {
      resetInteractiveContentInCache({
        client,
        contentType,
        classId: userClassId,
        interactiveContent: interactiveLessonClone,
      });
      onError?.();
    },
  });

  const duplicateSubActivityAsActivity = useCallback(
    (
      input: DuplicateActivitiesSubSectionAsSectionInput,
      subSection: Pluck<
        LessonActivitiesFragment,
        'sectionsOverview.subSectionsOverview'
      >
    ) => {
      if (!interactiveLesson) return;

      const requestId = createRequest(input.newDestinationSectionId);
      setInteractiveLessonClone(interactiveLesson);

      upsertSectionOverview({
        client,
        sectionOverview: {
          ...subSection,
          id: input.newDestinationSectionId,
          sort: input.destinationSectionNewSort,
          subSectionsOverview: null,
        },
      });
      addSectionOverviewToLesson({
        client,
        contentId: interactiveLesson.id,
        sectionId: input.newDestinationSectionId,
        contentType,
      });

      duplicate({
        variables: {
          input,
        },
      })
        .catch(() => {})
        .finally(() => {
          clearRequest(requestId);
        });
    },
    [
      contentType,
      client,
      duplicate,
      interactiveLesson,
      setInteractiveLessonClone,
      createRequest,
      clearRequest,
    ]
  );

  return {
    data,
    loading,
    error,
    duplicateSubActivityAsActivity,
  };
};

export const useDuplicateActivityAsSubActivity = ({
  contentId,
  contentType,
  onError,
  onCompleted,
}: {
  contentId: number;
  contentType: ActivitiesContentType;
  onCompleted?: (data: DuplicateActivitiesSectionAsSubSectionMutation) => void;
  onError?: () => void;
}) => {
  const { userClassId } = useGetInteractiveLessonParams();
  const { createRequest, clearRequest } = useActivityBuilderRequest();
  const interactiveLesson = useGetDraftActivitiesFromCache({
    contentId,
    contentType,
  });
  const { interactiveLessonClone, setInteractiveLessonClone } =
    useActivityBuilderApolloContext();

  const [duplicate, { data, error, loading, client }] = useLoggingMutation<
    DuplicateActivitiesSectionAsSubSectionMutation,
    DuplicateActivitiesSectionAsSubSectionMutationVariables
  >(DUPLICATE_ACTIVITIES_SECTION_AS_SUB_SECTION, {
    refetchQueries: [
      'GetLessonTeachingAndLearningSequence',
      'GetAssessmentTeachingAndLearningSequence',
      'GetDraftLessonActivities',
      'GetDraftAssessmentActivities',
    ],
    onCompleted: res => {
      if (interactiveLessonClone) {
        setInteractiveLessonClone(null);
      }
      onCompleted?.(res);
      modifyActivitiesPublishChanges({
        client,
        id: contentId,
        contentType,
      });
    },
    onError: () => {
      resetInteractiveContentInCache({
        client,
        contentType,
        classId: userClassId,
        interactiveContent: interactiveLessonClone,
      });
      onError?.();
    },
  });

  const duplicateActivityAsSubActivity = useCallback(
    (input: DuplicateActivitiesSectionAsSubSectionInput) => {
      if (!interactiveLesson) return;

      const requestId = createRequest(input.newDestinationSubSectionId);
      setInteractiveLessonClone(interactiveLesson);

      const sourceSectionOverview = interactiveLesson.sectionsOverview.find(
        s => s.id === input.sourceSectionId
      );

      if (sourceSectionOverview) {
        const subSections = sourceSectionOverview.subSectionsOverview || [];

        upsertSectionOverview({
          client,
          sectionOverview: {
            ...sourceSectionOverview,
            subSectionsOverview: [
              ...subSections,
              {
                ...sourceSectionOverview,
                parentSectionId: input.sourceSectionId,
                id: input.newDestinationSubSectionId,
                sort: input.destinationSubSectionNewSort,
                activityId: sourceSectionOverview.activityId,
              },
            ],
          },
        });
      }

      duplicate({
        variables: {
          input,
        },
      })
        .catch(() => {})
        .finally(() => {
          clearRequest(requestId);
        });
    },
    [
      duplicate,
      interactiveLesson,
      client,
      setInteractiveLessonClone,
      createRequest,
      clearRequest,
    ]
  );

  return {
    data,
    loading,
    error,
    duplicateActivityAsSubActivity,
  };
};
